import { useState } from "react";
import { ArrowSquareOut, Play, MusicNotes, SpotifyLogo } from "@phosphor-icons/react";
import { RC, ytThumb, ytUrl, spotifyEmbed } from "./rcApi";

const MODE_LABEL = { seul: "Seul", deux: "À deux", seul_et_deux: "Seul · à deux", ecoute: "Écoute" };

export function RcCard({ children, className = "", ...rest }) {
  return (
    <div {...rest} className={`rounded-2xl border p-5 sm:p-6 ${className}`} style={{ background: RC.card, borderColor: RC.line }}>
      {children}
    </div>
  );
}

export function RcTitle({ kicker, children, id }) {
  return (
    <div id={id} className="mb-5 scroll-mt-24">
      {kicker && <div className="text-[11px] uppercase tracking-[0.3em] mb-1.5" style={{ color: RC.gold }}>{kicker}</div>}
      <h2 className="text-2xl sm:text-3xl font-bold font-cabinet" style={{ color: RC.cream }}>{children}</h2>
    </div>
  );
}

export function RcButton({ children, variant = "primary", className = "", ...rest }) {
  const styles = variant === "primary"
    ? { background: RC.coral, color: "#1a0b10" }
    : variant === "gold" ? { background: RC.gold, color: "#1a1206" } : { background: "transparent", color: RC.cream, border: `1px solid ${RC.line}` };
  return (
    <button {...rest} style={styles}
      className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-base font-bold transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 ${className}`}>
      {children}
    </button>
  );
}

// Carte vidéo : vignette → lecteur intégré au clic, lien externe toujours présent.
export function VideoCard({ r, compact = false }) {
  const [open, setOpen] = useState(false);
  return (
    <RcCard data-testid={`rc-video-${r.id}`} className={compact ? "!p-4" : ""}>
      <div className="relative rounded-xl overflow-hidden aspect-video bg-black mb-3">
        {open ? (
          <iframe title={r.titre} src={`https://www.youtube-nocookie.com/embed/${r.youtube_id}?autoplay=1&rel=0`} className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        ) : (
          <button type="button" onClick={() => setOpen(true)} data-testid={`rc-video-play-${r.id}`} className="absolute inset-0 w-full h-full group" aria-label={`Lire ${r.titre}`}>
            <img src={ytThumb(r.youtube_id)} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" loading="lazy" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-16 h-16 rounded-full flex items-center justify-center shadow-xl" style={{ background: RC.coral }}><Play size={28} weight="fill" color="#1a0b10" /></span>
            </span>
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2 text-[11px] uppercase tracking-wider">
        {r.style && <Tag>{r.style}</Tag>}
        {r.niveau && <Tag>{r.niveau}</Tag>}
        {r.langue && <Tag>{r.langue}</Tag>}
        {r.mode && <Tag color={RC.turquoise}>{MODE_LABEL[r.mode] || r.mode}</Tag>}
        {r.priorite && <Tag color={RC.gold}>Prioritaire</Tag>}
        {r.brouillon && <Tag color={RC.muted}>Brouillon · à valider</Tag>}
      </div>
      <h3 className="font-bold text-lg leading-snug" style={{ color: RC.cream }}>{r.titre}</h3>
      {r.auteur && <div className="text-sm" style={{ color: RC.muted }}>{r.auteur}</div>}
      {r.notes && <p className="text-sm mt-2 leading-relaxed" style={{ color: RC.cream }}><span style={{ color: RC.gold }}>Note de l'encadrant · </span>{r.notes}</p>}
      {r.chapitres?.length > 0 && (
        <ul className="mt-3 grid gap-1 text-sm">
          {r.chapitres.map((c) => (
            <li key={c.t}><a href={ytUrl(r.youtube_id, c.t)} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: RC.turquoise }}>{c.t}</a> <span style={{ color: RC.muted }}>— {c.label}</span></li>
          ))}
        </ul>
      )}
      <a href={ytUrl(r.youtube_id)} target="_blank" rel="noreferrer" data-testid={`rc-video-ext-${r.id}`}
        className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" style={{ color: RC.gold }}>
        Ouvrir sur YouTube <ArrowSquareOut size={14} />
      </a>
    </RcCard>
  );
}

export function Tag({ children, color = RC.muted }) {
  return <span className="px-2 py-0.5 rounded-full border" style={{ color, borderColor: color + "66" }}>{children}</span>;
}

// Emplacement musical : lecteur Spotify seulement si un lien existe.
export function MusicBlock({ url, note, tracks = [], title }) {
  const embed = spotifyEmbed(url);
  return (
    <RcCard data-testid="rc-music-block">
      <div className="flex items-center gap-2 mb-3"><MusicNotes size={22} color={RC.turquoise} /><span className="font-bold" style={{ color: RC.cream }}>{title || "Musique pour pratiquer"}</span></div>
      {embed ? (
        <>
          <iframe title="Spotify" src={embed} width="100%" height="152" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" className="rounded-xl" />
          <a href={url} target="_blank" rel="noreferrer" data-testid="rc-open-playlist" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" style={{ color: RC.gold }}>
            <SpotifyLogo size={16} /> Ouvrir la playlist <ArrowSquareOut size={14} />
          </a>
        </>
      ) : (
        <p className="text-sm" style={{ color: RC.muted }} data-testid="rc-music-empty">La sélection musicale sera ajoutée prochainement.</p>
      )}
      {tracks.length > 0 && (
        <ul className="mt-4 divide-y" style={{ borderColor: RC.line }}>
          {tracks.map((t) => (
            <li key={t.id} className="py-2.5 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm" data-testid={`rc-track-${t.id}`}>
              <span className="font-semibold" style={{ color: RC.cream }}>{t.titre}</span>
              <span style={{ color: RC.muted }}>{t.artiste}</span>
              {t.style && <Tag>{t.style}</Tag>}
              {t.statut === "a_tester" && <Tag color={RC.gold}>À tester</Tag>}
              {t.exercice && <span className="w-full sm:w-auto" style={{ color: RC.turquoise }}>↳ {t.exercice}</span>}
              {t.spotify_url && <a href={t.spotify_url} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: RC.gold }}>Écouter</a>}
            </li>
          ))}
        </ul>
      )}
      {note && <p className="text-xs mt-3" style={{ color: RC.muted }}>{note}</p>}
    </RcCard>
  );
}

export function RulesBox({ regles = [] }) {
  return (
    <RcCard data-testid="rc-rules">
      <div className="text-[11px] uppercase tracking-[0.3em] mb-3" style={{ color: RC.gold }}>Règles du groupe</div>
      <ul className="grid sm:grid-cols-2 gap-2.5">
        {regles.map((r, i) => (
          <li key={i} className="flex gap-2.5 text-base leading-snug" style={{ color: RC.cream }}>
            <span className="mt-2 w-2 h-2 rounded-full shrink-0" style={{ background: RC.turquoise }} />{r}
          </li>
        ))}
      </ul>
    </RcCard>
  );
}

export function RcFooter() {
  return (
    <footer className="mt-16 py-8 text-center text-xs" style={{ color: RC.muted, borderTop: `1px solid ${RC.line}` }}>
      Rythmes & Connexions – Val-d'Or · un espace humain et inclusif · hébergé par Institut Impact Élite Martial
    </footer>
  );
}

export function RetourRencontre({ retour, print = false }) {
  if (!retour || !retour.publie) return null;
  const date = retour.date ? new Date(retour.date + "T12:00:00").toLocaleDateString("fr-CA", { day: "numeric", month: "long", year: "numeric" }) : "";
  const gold = print ? "#b8941f" : RC.gold;
  const muted = print ? "#52525b" : RC.muted;
  const List = ({ items, ordered }) => {
    const Tag_ = ordered ? "ol" : "ul";
    return <Tag_ className={`${ordered ? "list-decimal" : "list-disc"} pl-5 space-y-1 leading-relaxed`}>{(items || []).map((x, i) => <li key={i}>{x}</li>)}</Tag_>;
  };
  const Block = ({ k, children }) => (
    <div className={print ? "rounded-lg border border-zinc-300 p-3" : "rounded-2xl border p-4"} style={print ? {} : { background: RC.card, borderColor: RC.line }}>
      <div className="text-[10px] uppercase tracking-widest mb-1.5" style={{ color: gold }}>{k}</div>{children}
    </div>
  );
  return (
    <section data-testid="rc-retour-rencontre" className={print ? "mb-4" : ""}>
      {print ? <div className="font-black text-lg">{retour.titre}{date && <span className="text-zinc-500 font-normal text-sm"> · {date}</span>}</div>
        : <RcTitle kicker={`Après la rencontre${date ? " · " + date : ""}`}>{retour.titre}</RcTitle>}
      {retour.intro && <p className={`mb-3 ${print ? "text-zinc-700" : ""}`} style={print ? {} : { color: RC.turquoise }}>{retour.intro}</p>}
      <div className={`grid gap-3 ${print ? "grid-cols-2" : "md:grid-cols-2"}`}>
        {retour.vu?.length > 0 && <Block k="Ce qu'on a vu"><List items={retour.vu} /></Block>}
        {retour.retenir?.length > 0 && <Block k="3 choses à retenir"><List items={retour.retenir} /></Block>}
        {retour.exercice?.length > 0 && <div className={print ? "col-span-2" : "md:col-span-2"}><Block k="Ton exercice de 5 minutes à la maison"><List items={retour.exercice} ordered /></Block></div>}
      </div>
      {retour.prochaine && <p className="mt-3 text-sm" style={{ color: muted }}><b style={{ color: gold }}>La prochaine fois :</b> {retour.prochaine}</p>}
    </section>
  );
}
